/**
 * OAuth Service
 * Handles O365/Entra authentication
 * Supports two modes:
 * - Local: Direct Azure AD authentication (with PKCE for SPA)
 * - QA/Prod: AWS Cognito with Azure AD as identity provider
 */
import { generateCodeVerifier, generateCodeChallenge, storePKCE, getPKCEVerifier, clearPKCE, } from '../utils/pkce.js';
/**
 * Check if using direct Azure AD (local) or Cognito (QA/Prod)
 */
function isDirectAzureMode() {
    // If VITE_USE_COGNITO is explicitly set to false, use direct Azure
    // Otherwise, if Cognito domain is set, use Cognito
    const useCognito = import.meta.env.VITE_USE_COGNITO !== 'false';
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
    // Direct Azure mode: no Cognito domain OR explicitly disabled
    return !cognitoDomain || !useCognito;
}
/**
 * Check if a value is a placeholder (not a real credential)
 */
function isPlaceholder(value) {
    if (!value)
        return true;
    const lower = value.toLowerCase().trim();
    // Check for common placeholder patterns
    return (lower.includes('your-') ||
        lower.includes('placeholder') ||
        lower === '' ||
        lower.startsWith('example') ||
        lower.startsWith('xxx') ||
        lower === 'your-azure-tenant-id' ||
        lower === 'your-azure-app-client-id');
}
/**
 * Check if Azure AD authentication is configured
 */
export function isAzureADConfigured() {
    if (isDirectAzureMode()) {
        const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
        const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
        const hasTenantId = !!tenantId;
        const hasClientId = !!clientId;
        const isPlaceholderTenant = isPlaceholder(tenantId);
        const isPlaceholderClient = isPlaceholder(clientId);
        const configured = !!(tenantId && clientId && !isPlaceholderTenant && !isPlaceholderClient);
        console.log('[OAuth] isAzureADConfigured check:', {
            hasTenantId,
            hasClientId,
            isPlaceholderTenant,
            isPlaceholderClient,
            configured,
            tenantIdValue: tenantId ? `${tenantId.substring(0, 8)}...` : 'undefined',
            clientIdValue: clientId ? `${clientId.substring(0, 8)}...` : 'undefined',
            mode: 'direct Azure AD',
        });
        // Check that both exist and are not placeholders
        return configured;
    }
    else {
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
        // Check that both exist and are not placeholders
        return !!(cognitoDomain &&
            clientId &&
            !isPlaceholder(cognitoDomain) &&
            !isPlaceholder(clientId));
    }
}
/**
 * Get authentication configuration status message
 */
export function getAuthConfigMessage() {
    if (isDirectAzureMode()) {
        const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
        const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
        if (!tenantId && !clientId) {
            return 'Azure AD authentication is not configured. Please set VITE_AZURE_TENANT_ID and VITE_AZURE_CLIENT_ID in your .env.local file.';
        }
        else if (!tenantId) {
            return 'Azure AD Tenant ID is missing. Please set VITE_AZURE_TENANT_ID in your .env.local file.';
        }
        else if (!clientId) {
            return 'Azure AD Client ID is missing. Please set VITE_AZURE_CLIENT_ID in your .env.local file.';
        }
        return '';
    }
    else {
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
        if (!cognitoDomain && !clientId) {
            return 'Cognito authentication is not configured. Please set VITE_COGNITO_DOMAIN and VITE_COGNITO_CLIENT_ID in your environment variables.';
        }
        else if (!cognitoDomain) {
            return 'Cognito Domain is missing. Please set VITE_COGNITO_DOMAIN in your environment variables.';
        }
        else if (!clientId) {
            return 'Cognito Client ID is missing. Please set VITE_COGNITO_CLIENT_ID in your environment variables.';
        }
        return '';
    }
}
/**
 * Redirect to Azure AD for O365/Entra authentication
 */
export async function loginWithO365() {
    const redirectUri = `${window.location.origin}/auth/callback`;
    if (isDirectAzureMode()) {
        // Local: Direct Azure AD authentication with PKCE (required for SPA)
        const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
        const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
        console.log('[OAuth] Starting login flow', {
            redirectUri,
            hasTenantId: !!tenantId,
            hasClientId: !!clientId,
            origin: window.location.origin,
        });
        if (!tenantId || !clientId) {
            const errorMsg = getAuthConfigMessage();
            console.error('[OAuth] Missing credentials:', errorMsg);
            throw new Error(errorMsg);
        }
        // Check for placeholders
        if (isPlaceholder(tenantId) || isPlaceholder(clientId)) {
            const errorMsg = 'Azure AD credentials appear to be placeholders. Please update .env.local with real credentials.';
            console.error('[OAuth] Placeholder credentials detected');
            throw new Error(errorMsg);
        }
        // Generate PKCE values for SPA authentication
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        // Store PKCE values BEFORE redirect
        storePKCE(codeVerifier, codeChallenge);
        // Verify storage immediately
        const storedVerifier = sessionStorage.getItem('pkce_verifier');
        if (!storedVerifier) {
            console.error('[OAuth] PKCE verifier not found in sessionStorage');
            throw new Error('Failed to store PKCE verifier. Cannot proceed with authentication.');
        }
        // Build Azure AD authorization URL with PKCE
        const params = new URLSearchParams({
            client_id: clientId,
            response_type: 'code',
            scope: 'openid email profile User.Read',
            redirect_uri: redirectUri,
            response_mode: 'query',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
        });
        const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`;
        console.log('[OAuth] Redirecting to Azure AD', {
            tenantId: tenantId.substring(0, 8) + '...',
            clientId: clientId.substring(0, 8) + '...',
            redirectUri,
        });
        // Small delay to ensure sessionStorage is written (though it should be synchronous)
        await new Promise((resolve) => setTimeout(resolve, 100));
        window.location.href = authUrl;
    }
    else {
        // QA/Prod: Cognito Hosted UI with Azure AD as identity provider
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
        // Use the actual identity provider name from Cognito (default: ozgenellc-ozvcs-O365-qa1 for VCS)
        // IMPORTANT: Provider name must match exactly what's configured in Cognito (case-sensitive)
        let provider = import.meta.env.VITE_O365_PROVIDER_NAME || 'ozgenellc-ozvcs-O365-qa1';
        // Fix: Ensure provider name matches Cognito exactly (case-sensitive)
        // Cognito provider is: ozgenellc-ozvcs-O365-prod (capital O)
        if (provider.includes('0365')) {
            console.warn('[OAuth] Provider name has zero instead of O - fixing:', provider);
            provider = provider.replace('0365', 'O365');
        }
        // Debug: Log the provider name to help diagnose issues
        console.log('[OAuth] Provider name check:', {
            fromEnv: import.meta.env.VITE_O365_PROVIDER_NAME,
            finalProvider: provider,
            envKeys: Object.keys(import.meta.env).filter((k) => k.includes('O365') || k.includes('COGNITO')),
        });
        if (!cognitoDomain || !clientId) {
            throw new Error('Cognito authentication is not configured. Please set VITE_COGNITO_DOMAIN and VITE_COGNITO_CLIENT_ID environment variables.');
        }
        // Log the exact redirect URI being used for debugging
        console.log('[OAuth] Cognito login - redirect URI:', {
            redirectUri,
            origin: window.location.origin,
            href: window.location.href,
            cognitoDomain,
            clientId: clientId.substring(0, 8) + '...',
            provider,
        });
        // Build Cognito Hosted UI URL with O365 identity provider
        const params = new URLSearchParams({
            client_id: clientId,
            response_type: 'code',
            scope: 'email openid profile',
            redirect_uri: redirectUri,
            identity_provider: provider,
        });
        const authUrl = `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
        console.log('[OAuth] Full auth URL:', authUrl);
        console.log('[OAuth] About to redirect to Cognito...');
        // Use a small delay to ensure console logs are visible
        await new Promise((resolve) => setTimeout(resolve, 100));
        // Redirect
        window.location.href = authUrl;
    }
}
/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForTokens(code) {
    const redirectUri = `${window.location.origin}/auth/callback`;
    if (isDirectAzureMode()) {
        // Local: Direct Azure AD token exchange with PKCE (required for SPA)
        const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
        const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
        console.log('[OAuth] Exchanging code for tokens', {
            hasTenantId: !!tenantId,
            hasClientId: !!clientId,
            redirectUri,
        });
        if (!tenantId || !clientId) {
            throw new Error('Azure AD authentication is not configured. Please set VITE_AZURE_TENANT_ID and VITE_AZURE_CLIENT_ID environment variables.');
        }
        // Get PKCE verifier (must match the challenge used in authorization)
        const codeVerifier = getPKCEVerifier();
        if (!codeVerifier) {
            console.error('[OAuth] PKCE verifier missing! Available sessionStorage:', Object.keys(sessionStorage));
            clearPKCE();
            throw new Error('PKCE verifier not found. This usually means the login flow was interrupted. Please try logging in again from the login page.');
        }
        const tokenUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`;
        const params = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier, // PKCE: send verifier instead of client_secret
        });
        // Clear PKCE after use (one-time use)
        clearPKCE();
        console.log('[OAuth] Requesting tokens from Azure AD');
        const response = await fetch(tokenUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[OAuth] Token exchange failed:', {
                status: response.status,
                statusText: response.statusText,
                error: errorText,
            });
            throw new Error(`Failed to exchange code for tokens (${response.status}): ${errorText}`);
        }
        const tokenData = await response.json();
        console.log('[OAuth] Token exchange successful');
        return tokenData;
    }
    else {
        // QA/Prod: Cognito token exchange
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
        if (!cognitoDomain || !clientId) {
            throw new Error('Cognito authentication is not configured. Please set VITE_COGNITO_DOMAIN and VITE_COGNITO_CLIENT_ID environment variables.');
        }
        const tokenUrl = `https://${cognitoDomain}/oauth2/token`;
        const params = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code,
            redirect_uri: redirectUri,
        });
        const response = await fetch(tokenUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: params.toString(),
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[OAuth] Cognito token exchange failed:', {
                status: response.status,
                statusText: response.statusText,
                error: errorText,
                tokenUrl,
            });
            throw new Error(`Failed to exchange code for tokens (${response.status}): ${errorText}`);
        }
        const tokenData = await response.json();
        console.log('[OAuth] Cognito token exchange successful');
        return tokenData;
    }
}
/**
 * Sign out from Cognito
 * Redirects to Cognito logout endpoint
 */
export async function signOut() {
    const useCognito = import.meta.env.VITE_USE_COGNITO === 'true';
    if (useCognito) {
        const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN;
        const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
        const logoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
        // Redirect to Cognito logout
        window.location.href = logoutUrl;
    }
    else {
        // For Azure AD direct mode, just clear local storage
        // Azure AD doesn't have a hosted logout UI for SPAs
        window.location.href = '/login';
    }
}
//# sourceMappingURL=oauth.service.js.map